import React, { MouseEventHandler, useEffect, useMemo, useRef, useState } from 'react';

import { NavLink, useLocation } from 'react-router-dom';
import Lottie from 'react-lottie-player';
import type {
  AnimationItem,
} from 'lottie-web';
import cx from 'classnames';
import styles from './menu.module.scss';
import _ from 'lodash';
import { moduleService } from 'src/utils/module.service';
import { useSelector } from 'react-redux';
import { useTransition, animated } from '@react-spring/web';
import SettingsSideMenu from './settingsMenu';
import CoachingSideMenu from './coachingMenu';
import { StoryLinePlan } from 'src/pages/experiences/components/storyLinePlan';

interface MainMenuProps {
  toggleCompose?: () => void;
  isDisabled?: boolean;
  expanded: boolean
  setExpanded?: (expanded: boolean) => void;
}

export const MainMenu: React.FC<MainMenuProps> = ({ toggleCompose, isDisabled = false, expanded, setExpanded }) => {
  const [activeModule, setActiveModule] = useState("");

  const account = useSelector((state: any) => _.get(state, 'accounts.item'));
  const hideSideMenu = useSelector((state: any) => _.get(state, 'hub.hideSideMenu'));
  const workspacesFetched = useSelector((state: any) => _.get(state, 'experiences.workspaces.fetched', false));

  const menuContainerRef = useRef<HTMLDivElement>(null);
  const menuStartRef = useRef<HTMLSpanElement>(null);
  const menuEndRef = useRef<HTMLSpanElement>(null);
  const location = useLocation();

  const url = window.location.pathname;
  const isLiteApp = url.startsWith("/lite");
  
  useEffect(() => {
    const pathname = location.pathname.split("/")[1];
    setActiveModule(pathname);

  },[location.pathname]);

  const handleModuleClick = (key: string) => {
    if (isDisabled) return;

    if (key === "compose") {
      toggleCompose?.();
    }
  };

  const modules = moduleService(account, isLiteApp, workspacesFetched);
  const isSettingsActive = activeModule === "settings";
  const isCoachingActive = activeModule === "coaching";
  const isStoryLineActive = activeModule === "storyline";

    const handleMouseEnter = (e) => {
    if (!isDisabled && setExpanded) {
      setExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isDisabled && setExpanded) {
      setExpanded(false);
    }
  };

  return (
    <div className={cx(styles.menu, {
      [styles.expanded]: expanded
    }
    )} >
 
      <div className={cx(styles.mainMenu,{
        [styles.settingsActive] : isSettingsActive || isCoachingActive || isStoryLineActive
      })} onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>

        <div className={styles.primaryMenu} ref={menuContainerRef}>
          <span ref={menuStartRef}></span>
          {modules.map((module) => (
            <MenuItem
              id={module.id}
              key={module.id}
              to={module.to}
              icon={module.icon}
              label={module.label}
              onClick={() => handleModuleClick(module.id)}
              isDisabled={isDisabled}
              expanded={expanded}
            />
          ))}
          <span ref={menuEndRef}></span>
        </div>


        <div className={styles.secondaryMenu}>
          {!isLiteApp && (
            <MenuItem id="settings" to="/settings" icon="/images/menu/settings.json" label="Settings" isDisabled={isDisabled} expanded={expanded} />
          )}
          <MenuItem id="intercom_launcher" to="" icon="/images/menu/talkToUs.json" label="Talk to us" isDisabled={isDisabled} expanded={expanded} />
        </div>
      </div>
      <div style={{display : (isSettingsActive && !hideSideMenu) ? "block" : "none" }}>
        <SettingsSideMenu/>
      </div>
      <div style={{ display: (isCoachingActive) ? "flex" : "none", justifyContent: "center"}}
        onMouseEnter={() => isCoachingActive && setExpanded?.(true)}
        onMouseLeave={() => isCoachingActive && setExpanded?.(false)}
      >
        <CoachingSideMenu expanded={expanded} />
      </div>
      {isStoryLineActive && (
        <StoryLinePlan />
      )}
    </div>
  );
}

interface MenuItemProps {
  id: string;
  icon: string;
  label: string;
  to: string;
  onClick?: () => void;
  isDisabled: boolean;
  expanded: boolean
}

export const MenuItem: React.FC<MenuItemProps> = ({ id, icon, to, label, onClick, isDisabled, expanded }) => {
  const iconRef = React.useRef<AnimationItem>(null);

  const handleMouseEnter = () => {
    if (isDisabled) {
      return;
    }

    iconRef.current?.play();
  }

  const handleMouseLeave = () => {
    iconRef.current?.goToAndStop(0);
  }

  const handleNavLink: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (isDisabled) {
      e.preventDefault();
    }
  }

  const opacityTransition = useTransition(expanded, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  })

  return (
    <>
      {_.isEmpty(to) ? (
        <div id={id} className={cx(styles.menuItem, { [styles.menuItemDisabled]: isDisabled })} onClick={onClick} onMouseOver={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <Lottie
            className={styles.icon}
            ref={iconRef}
            path={icon}
            loop={false}
            rendererSettings={{
              preserveAspectRatio: "xMidYMid slice",
              className: "lottie-icon",
            }}
            style={{
              width: 32,
              height: 32,
            }}
          />
          <span className={cx(styles.iconLabel, { [styles.iconLabelExpanded]: expanded })}>
            {label}
          </span>
        </div>
      ) : (
        <NavLink id={id} className={cx(styles.menuItem, {
          [styles.activeMenuItem]: window.location.pathname.split('/')[1] === id,
          [styles.menuItemDisabled]: isDisabled,
        })} to={to} onClick={handleNavLink} onMouseOver={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <Lottie
            className={styles.icon}
            ref={iconRef}
            path={icon}
            loop={false}
            rendererSettings={{
              preserveAspectRatio: "xMidYMid slice",
              className: "lottie-icon",
            }}
            style={{
              width: 32,
              height: 32,
            }}
            onMouseOver={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
          <span className={cx(styles.iconLabel, { [styles.iconLabelExpanded]: expanded })}>
            {label}
          </span>
        </NavLink>
      )}
    </>
  );
}
